import { useState } from 'react';
import { Folder, Pencil, Plus, Trash2 } from 'lucide-react';
import { useI18n } from '@/i18n';
import { api, toApiError } from '@/lib/api';
import { keys, useCategories, useInvalidate } from '@/lib/queries';
import { useToast } from '@/components/ui/Toast';
import { slugify } from '@/lib/seo';
import type { CategoryItem } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/Button';
import { Field, Input, MonoInput } from '@/components/ui/Field';
import { Modal, Tooltip } from '@/components/ui/primitives';
import { Chip } from '@/components/ui/Chip';
import { EmptyState, TableSkeleton } from '@/components/ui/Feedback';

/**
 * Categories have their own landing pages (decision B4), so their names and
 * slugs are editorial. `key` is shown but never editable: it is what the site
 * filters on, and letting a translator rename it is precisely the defect this
 * design removed.
 */
export function Categories() {
  const { t, lang } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();
  const { data, isLoading } = useCategories();

  const [editing, setEditing] = useState<CategoryItem | 'new' | null>(null);
  const [removing, setRemoving] = useState<CategoryItem | null>(null);

  const rows = data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t.categories.title}
        subtitle={t.categories.subtitle}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            {t.categories.new}
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-content px-6 pb-12">
        <div className="card overflow-hidden">
          {isLoading ? (
            <TableSkeleton rows={3} cols={3} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Folder className="h-6 w-6" />}
              title={t.categories.emptyTitle}
              hint={t.categories.emptyHint}
              action={
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
                  {t.categories.emptyAction}
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((category) => (
                <li
                  key={category.id}
                  className="row group flex items-center gap-4 px-4 py-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-500/10 text-brand-500 dark:text-brand-300">
                    <Folder aria-hidden className="h-4 w-4" strokeWidth={1.85} />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body font-medium text-strong">
                      {category.name[lang] ?? category.name.uz ?? category.key}
                    </span>
                    <span className="block truncate font-mono text-code text-faint">
                      /news/category/{category.slug}
                    </span>
                  </span>

                  <Chip tone="quiet" mono className="hidden sm:inline-flex">
                    {category.key}
                  </Chip>

                  <Chip tone={category.articleCount > 0 ? 'neutral' : 'quiet'} mono>
                    {t.categories.countLabel(category.articleCount)}
                  </Chip>

                  {/*
                    Always visible, not hover-only. These are the only way to
                    act on a row: hidden until hover means a new user cannot
                    find them at all, and a keyboard user finds them last.
                  */}
                  <span className="flex shrink-0 items-center gap-1">
                    <Tooltip label={t.common.edit}>
                      <Button size="sm" variant="ghost" aria-label={t.common.edit} onClick={() => setEditing(category)}>
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.85} />
                      </Button>
                    </Tooltip>
                    <Tooltip label={t.common.delete}>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={t.common.delete}
                        className="text-faint hover:text-accent-600 dark:hover:text-accent-400"
                        onClick={() => setRemoving(category)}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.85} />
                      </Button>
                    </Tooltip>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {editing && (
        <CategoryDialog
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void invalidate(keys.categories);
            setEditing(null);
            toast({ tone: 'success', message: t.common.saved });
          }}
        />
      )}

      <Modal
        open={Boolean(removing)}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t.common.delete}
        description={removing ? (removing.name[lang] ?? removing.key) : ''}
        footer={
          <>
            <Button variant="outline" onClick={() => setRemoving(null)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!removing) return;
                try {
                  await api.delete(`/categories/${removing.id}`);
                  void invalidate(keys.categories);
                  setRemoving(null);
                  toast({ tone: 'info', message: t.common.deleted });
                } catch (error) {
                  toast({ tone: 'error', message: toApiError(error).message });
                }
              }}
            >
              {t.common.delete}
            </Button>
          </>
        }
      >
        {removing && removing.articleCount > 0 && (
          <p className="text-ui text-amber-600 dark:text-amber-400">{t.categories.deleteHasArticles}</p>
        )}
      </Modal>
    </>
  );
}

function CategoryDialog({
  category,
  onClose,
  onSaved,
}: {
  category: CategoryItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [nameUz, setNameUz] = useState(category?.name.uz ?? '');
  const [nameRu, setNameRu] = useState(category?.name.ru ?? '');
  const [nameEn, setNameEn] = useState(category?.name.en ?? '');
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { nameUz: nameUz || null, nameRu: nameRu || null, nameEn: nameEn || null, slug: slug || slugify(nameUz || nameRu) };
      if (category) await api.patch(`/categories/${category.id}`, payload);
      else await api.post('/categories', { ...payload, key: slugify(nameEn || nameRu || nameUz) });
      onSaved();
    } catch (error) {
      toast({ tone: 'error', message: toApiError(error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={category ? t.common.edit : t.categories.new}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            {t.common.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={`${t.categories.nameLabel} — UZ`}>
          {({ id }) => (
            <Input
              id={id}
              autoFocus
              value={nameUz}
              onChange={(event) => {
                setNameUz(event.target.value);
                if (!category && !slug) setSlug(slugify(event.target.value));
              }}
            />
          )}
        </Field>
        <Field label={`${t.categories.nameLabel} — RU`}>
          {({ id }) => <Input id={id} value={nameRu} onChange={(event) => setNameRu(event.target.value)} />}
        </Field>
        <Field label={`${t.categories.nameLabel} — EN`} optional>
          {({ id }) => <Input id={id} value={nameEn} onChange={(event) => setNameEn(event.target.value)} />}
        </Field>
        <Field label={t.editor.slugLabel} hint="ipost.uz/uz/news/category/…">
          {({ id, describedBy }) => (
            <MonoInput
              id={id}
              aria-describedby={describedBy}
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              onBlur={() => setSlug(slugify(slug))}
            />
          )}
        </Field>
        {category && (
          <Field label={t.categories.keyLabel} hint={t.categories.keyHint}>
            {({ id, describedBy }) => (
              <MonoInput id={id} aria-describedby={describedBy} value={category.key} disabled readOnly />
            )}
          </Field>
        )}
      </div>
    </Modal>
  );
}
